import React, { useState } from 'react';

import styled from '@emotion/styled';

import {
  LookupPriceInitialValues,
  LookupPriceSchema,
} from '../../../../models/dashboard/driver-lookup/lookup-price';
import { useAppSelector } from '../../../../redux/store';
import { formatNairaWithoutDecimal } from '../../../../utils/utils';
import StatusCell from '../../../Badge/StatusCell';
import { FormSubmitButton } from '../../../Button/FormSubmitButton';
import {
  BundleRadioField,
  BundleRadioFieldGroup,
} from '../../../FormCom/BundleRadioField';
import { FormComponent } from '../../../FormCom/FormComponent';

interface TriggerBundlePaymentFormProps {
  handleOnSubmit: (values: any, actions: any) => void;
  handleAmountFieldChange: (amount: string) => void;
  loading: boolean;
  amount: number;
  price: number;
}

const TriggerBundlePaymentForm: React.FC<TriggerBundlePaymentFormProps> = ({
  handleOnSubmit,
  handleAmountFieldChange,
  loading,
  amount,
}) => {
  const { lookupConfig } = useAppSelector((state) => state.lookupConfigData);
  const [selectedNewPrice, setSelectedNewPrice] = useState<number | null>(null);

  const onValueChange = (value: any) => {
    const bundle = lookupConfig?.pricingBundle?.find(
      (b) => `${b.bundleId}` === value,
    );
    setSelectedNewPrice(bundle?.newPrice ?? null);
    handleAmountFieldChange(value);
  };

  return (
    <FormComponent
      initialValues={LookupPriceInitialValues}
      schema={LookupPriceSchema}
      onSubmit={handleOnSubmit}
    >
      <BundleRadioFieldGroup name='amount' onValueChange={onValueChange}>
        {lookupConfig?.pricingBundle?.map((bundle) => {
          const bundleId = bundle.bundleId ?? 0;
          const newPrice = bundle.newPrice ?? 0;
          const originalPrice = bundle.originalPrice ?? 0;
          const discountPercentage = bundle.discount ?? 0;
          const noOfLookups = bundle.numberOfLookups ?? 0;
          const unitPrice = lookupConfig?.unitPrice ?? 0;

          return (
            <BundleRadioField
              name='amount'
              value={`${bundleId}`}
              key={bundle.bundleId}
              isRecommended={bundle.isRecommended}
            >
              <BundleArea>
                <BundleHeader>
                  <div>{`${noOfLookups} Look-ups`}</div>
                  <StatusCell
                    data={{ percentage: `${discountPercentage}% OFF` }}
                    statusKey='percentage'
                    plain
                    customClasses='percentage'
                  />
                </BundleHeader>

                <PriceChanges>
                  <Crossed>{`${formatNairaWithoutDecimal(originalPrice)}`}</Crossed>
                  <div>{`${formatNairaWithoutDecimal(newPrice)}`}</div>
                </PriceChanges>

                <PriceRatingForBundle>{`${formatNairaWithoutDecimal(unitPrice)} per look-up`}</PriceRatingForBundle>
              </BundleArea>
            </BundleRadioField>
          );
        })}
      </BundleRadioFieldGroup>

      <FormSubmitButton loading={loading}>
        {`Pay ${formatNairaWithoutDecimal(selectedNewPrice ?? amount)} via Paystack`}
      </FormSubmitButton>
    </FormComponent>
  );
};

export default TriggerBundlePaymentForm;

const BundleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BundleHeader = styled.div`
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  line-height: 14px;
  gap: 8px;
`;

const PriceChanges = styled.div`
  font-weight: 600;
  font-size: 13px;
  line-height: 13px;
  color: #5b91f5;
  display: flex;
  gap: 6px;
`;

const Crossed = styled.div`
  font-weight: 500;
  font-size: 12px;
  line-height: 12px;
  text-decoration: line-through;
  color: #9ca3af;
`;

const PriceRatingForBundle = styled.div`
  font-weight: 400;
  font-size: 10px;
  line-height: 10px;
  color: #6b7280;
`;
